import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Save,
  ChevronDown,
  Check,
  Pencil,
  Trash2,
  Copy,
  Bookmark,
  RotateCcw,
  MoreHorizontal,
  AlertCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useAnalyticsPreferencesStore,
  type FilterPreset,
  type FilterValues,
  SYSTEM_DEFAULTS,
} from '@/stores/analytics-preferences-store';
import { cn } from '@/lib/utils';

interface PresetSelectorProps {
  /** Current filter values to save when creating/updating a preset */
  currentFilters: FilterValues;
  /** Callback when a preset is loaded */
  onLoadPreset: (preset: FilterPreset) => void;
  /** Callback when reset to defaults is clicked */
  onResetToDefaults: () => void;
  /** Optional class name */
  className?: string;
}

/**
 * Check if current filters match a preset's saved values
 */
function filtersMatchPreset(filters: FilterValues, preset: FilterPreset): boolean {
  return (
    filters.locationId === preset.locationId &&
    filters.deviceId === preset.deviceId &&
    filters.interval === preset.interval &&
    filters.timezone === preset.timezone &&
    filters.dateRangeDays === preset.dateRangeDays
  );
}

/**
 * Check if current filters match system defaults
 */
function filtersMatchDefaults(filters: FilterValues): boolean {
  return (
    filters.locationId === SYSTEM_DEFAULTS.locationId &&
    filters.deviceId === SYSTEM_DEFAULTS.deviceId &&
    filters.interval === SYSTEM_DEFAULTS.interval &&
    filters.timezone === SYSTEM_DEFAULTS.timezone &&
    filters.dateRangeDays === SYSTEM_DEFAULTS.dateRangeDays
  );
}

export function PresetSelector({
  currentFilters,
  onLoadPreset,
  onResetToDefaults,
  className = '',
}: PresetSelectorProps) {
  const {
    presets,
    activePresetId,
    createPreset,
    updatePreset,
    deletePreset,
    renamePreset,
    setActivePreset,
    duplicatePreset,
  } = useAnalyticsPreferencesStore();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<FilterPreset | null>(null);
  const [presetName, setPresetName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  const activePreset = presets.find((p) => p.id === activePresetId) || null;

  // Check if current filters have been modified from the active preset
  const isModified = useMemo(() => {
    if (activePreset) {
      return !filtersMatchPreset(currentFilters, activePreset);
    }
    return !filtersMatchDefaults(currentFilters);
  }, [currentFilters, activePreset]);

  // Validate preset name
  const validateName = (name: string, excludeId?: string): string | null => {
    const trimmed = name.trim();
    if (!trimmed) {
      return 'Name is required';
    }
    if (trimmed.length < 2) {
      return 'Name must be at least 2 characters';
    }
    if (trimmed.length > 50) {
      return 'Name must be 50 characters or less';
    }
    const duplicate = presets.find(
      (p) => p.name.toLowerCase() === trimmed.toLowerCase() && p.id !== excludeId
    );
    if (duplicate) {
      return 'A preset with this name already exists';
    }
    return null;
  };

  const handleCreatePreset = () => {
    const error = validateName(presetName);
    if (error) {
      setNameError(error);
      return;
    }
    const newPreset = createPreset(presetName.trim(), currentFilters);
    setIsCreateDialogOpen(false);
    setPresetName('');
    setNameError(null);
    onLoadPreset(newPreset);
    setIsOpen(false);
  };

  const handleRenamePreset = () => {
    if (!selectedPreset) return;
    const error = validateName(presetName, selectedPreset.id);
    if (error) {
      setNameError(error);
      return;
    }
    renamePreset(selectedPreset.id, presetName.trim());
    setIsRenameDialogOpen(false);
    setSelectedPreset(null);
    setPresetName('');
    setNameError(null);
  };

  const handleDeletePreset = () => {
    if (!selectedPreset) return;
    deletePreset(selectedPreset.id);
    setIsDeleteDialogOpen(false);
    setSelectedPreset(null);
  };

  const handleUpdatePreset = () => {
    if (!selectedPreset) return;
    updatePreset(selectedPreset.id, {
      locationId: currentFilters.locationId,
      locationName: currentFilters.locationName,
      deviceId: currentFilters.deviceId,
      deviceName: currentFilters.deviceName,
      interval: currentFilters.interval,
      timezone: currentFilters.timezone,
      dateRangeDays: currentFilters.dateRangeDays,
      tab: currentFilters.tab,
    });
    setIsUpdateDialogOpen(false);
    setSelectedPreset(null);
  };

  const handleLoadPreset = (preset: FilterPreset) => {
    setActivePreset(preset.id);
    onLoadPreset(preset);
    setIsOpen(false);
  };

  const handleResetToDefaults = () => {
    setActivePreset(null);
    onResetToDefaults();
    setIsOpen(false);
  };

  const openRenameDialog = (preset: FilterPreset) => {
    setSelectedPreset(preset);
    setPresetName(preset.name);
    setNameError(null);
    setIsRenameDialogOpen(true);
  };

  const openDeleteDialog = (preset: FilterPreset) => {
    setSelectedPreset(preset);
    setIsDeleteDialogOpen(true);
  };

  const openUpdateDialog = (preset: FilterPreset) => {
    setSelectedPreset(preset);
    setIsUpdateDialogOpen(true);
  };

  const handleDuplicate = (preset: FilterPreset) => {
    duplicatePreset(preset.id);
  };

  const handleSaveCurrentAsPreset = () => {
    setPresetName('');
    setNameError(null);
    setIsCreateDialogOpen(true);
  };

  // Get display label for the button
  const buttonLabel = activePreset ? activePreset.name : 'Presets';

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className={cn('gap-2 min-w-[120px] justify-between', className)}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Bookmark className="h-4 w-4 shrink-0" />
              <span className="truncate max-w-[100px]">{buttonLabel}</span>
              {isModified && (
                <Badge 
                  variant="secondary" 
                  className="h-5 px-1.5 text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200"
                >
                  Modified
                </Badge>
              )}
            </div>
            <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          {/* Header with Save button */}
          <div className="p-3 border-b">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">Filter Presets</h4>
              <Button 
                size="sm" 
                variant="default"
                className="h-7 gap-1.5 text-xs"
                onClick={handleSaveCurrentAsPreset}
              >
                <Save className="h-3.5 w-3.5" />
                Save Current
              </Button>
            </div>
            {isModified && activePreset && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Current filters differ from "{activePreset.name}"
              </p>
            )}
          </div>

          <ScrollArea className="max-h-[300px]">
            {/* System Defaults */}
            <div className="p-1.5">
              <button
                type="button"
                onClick={handleResetToDefaults}
                className={cn(
                  'w-full flex items-center gap-3 p-2.5 rounded-md text-left transition-colors',
                  'hover:bg-accent',
                  !activePresetId && 'bg-accent'
                )}
              >
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-muted">
                  <RotateCcw className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">System Defaults</div>
                  <div className="text-xs text-muted-foreground">
                    All locations • {SYSTEM_DEFAULTS.interval} • {SYSTEM_DEFAULTS.dateRangeDays} days
                  </div>
                </div>
                {!activePresetId && (
                  <Check className="h-4 w-4 text-primary shrink-0" />
                )}
              </button>
            </div>

            {presets.length > 0 && <Separator />}

            {/* Saved Presets */}
            <div className="p-1.5 space-y-0.5">
              {presets.map((preset) => {
                const isActive = activePresetId === preset.id;
                const presetModified = isActive && isModified;

                return (
                  <div 
                    key={preset.id} 
                    className={cn(
                      'flex items-center gap-2 p-2 rounded-md transition-colors',
                      'hover:bg-accent',
                      isActive && 'bg-accent'
                    )}
                  >
                    {/* Preset info - clickable to load */}
                    <button
                      type="button"
                      onClick={() => handleLoadPreset(preset)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                      <div className={cn(
                        'flex items-center justify-center h-8 w-8 rounded-md',
                        isActive ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      )}>
                        <Bookmark className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{preset.name}</span>
                          {presetModified && (
                            <Badge 
                              variant="outline" 
                              className="h-4 px-1 text-[9px] border-amber-300 text-amber-600 dark:border-amber-700 dark:text-amber-400"
                            >
                              modified
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {preset.locationName || 'All locations'} • {preset.interval} • {preset.dateRangeDays}d
                        </div>
                      </div>
                      {isActive && !presetModified && (
                        <Check className="h-4 w-4 text-primary shrink-0" />
                      )}
                    </button>

                    {/* Actions menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        {presetModified && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              openUpdateDialog(preset);
                            }}
                            className="gap-2"
                          >
                            <Save className="h-4 w-4" />
                            Save Changes
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            openRenameDialog(preset);
                          }}
                          className="gap-2"
                        >
                          <Pencil className="h-4 w-4" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicate(preset);
                          }}
                          className="gap-2"
                        >
                          <Copy className="h-4 w-4" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteDialog(preset);
                          }}
                          className="gap-2 text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>

            {/* Empty state */}
            {presets.length === 0 && (
              <div className="p-6 text-center">
                <Bookmark className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No saved presets</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Click "Save Current" to create your first preset
                </p>
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {/* Create Preset Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Save Filter Preset</DialogTitle>
            <DialogDescription>
              Save your current filter settings as a preset for quick access later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name">Preset Name</Label>
              <Input
                id="preset-name"
                placeholder="e.g., Main Office - Daily"
                value={presetName}
                onChange={(e) => {
                  setPresetName(e.target.value);
                  setNameError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreatePreset();
                }}
                autoFocus
                className={nameError ? 'border-destructive' : ''}
              />
              {nameError && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {nameError}
                </p>
              )}
            </div>
            <div className="rounded-md bg-muted p-3 space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Filters to save:</p>
              <ul className="text-xs space-y-1 text-muted-foreground">
                <li className="flex items-center justify-between">
                  <span>Location</span>
                  <span className="font-medium text-foreground">{currentFilters.locationName || 'All locations'}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span>Device</span>
                  <span className="font-medium text-foreground">{currentFilters.deviceName || 'All devices'}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span>Interval</span>
                  <span className="font-medium text-foreground">{currentFilters.interval}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span>Date Range</span>
                  <span className="font-medium text-foreground">Last {currentFilters.dateRangeDays} days</span>
                </li>
                <li className="flex items-center justify-between">
                  <span>Timezone</span>
                  <span className="font-medium text-foreground">{currentFilters.timezone}</span>
                </li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreatePreset} disabled={!presetName.trim()}>
              Save Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Preset Dialog */}
      <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Rename Preset</DialogTitle>
            <DialogDescription>
              Enter a new name for "{selectedPreset?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rename-preset">New Name</Label>
              <Input
                id="rename-preset"
                value={presetName}
                onChange={(e) => {
                  setPresetName(e.target.value);
                  setNameError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenamePreset();
                }}
                autoFocus
                className={nameError ? 'border-destructive' : ''}
              />
              {nameError && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {nameError}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRenamePreset} disabled={!presetName.trim()}>
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Preset Confirmation Dialog */}
      <AlertDialog open={isUpdateDialogOpen} onOpenChange={setIsUpdateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Preset</AlertDialogTitle>
            <AlertDialogDescription>
              Save your current filter settings to "{selectedPreset?.name}"?
              This will overwrite the existing preset configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUpdatePreset}>
              Save Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Preset Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Preset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedPreset?.name}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePreset}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
